import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { withTransaction } from '@datorama/akita';
import { tap } from 'rxjs/operators';
import { LegalEntity, OwnerStatus } from './legal-entity.model';
import { LegalEntityStore } from './legal-entity-store.service';
import { AppConfigQuery } from '../../../../core/state/app-config/app-config.query';
import { Observable } from 'rxjs';
import { Cacheable } from 'ts-cacheable';

@Injectable({ providedIn: 'root' })
export class LegalEntityService {
    constructor(
        private store: LegalEntityStore,
        private http: HttpClient,
        private config: AppConfigQuery
    ) {}

    @Cacheable({
        maxAge: 1000
    })
    get() {
        return this.http
            .get<LegalEntity[]>(`${this.config.get().adminBaseUrl}/v1/owners`)
            .pipe(
                withTransaction((entities) => {
                    this.store.set(entities);
                    // set all as active if none is selected TODO: should only be set if not saved by user
                    if (this.store.getValue().active.length === 0) {
                        this.setSelectedState(entities, true);
                    }
                })
            );
    }

    /** Get single owner by id.
     * @param id - owner id
     * @returns - observable for request
     */
    getById(id: LegalEntity['ownerId']) {
        return this.http
            .get<LegalEntity>(`${this.config.get().adminBaseUrl}/v1/owners/${id}`)
            .pipe(
                tap((owner) => {
                    this.store.upsert([owner.ownerId], owner);
                })
            );
    }

    /** Create new owner - create a new owner.
     * @param data - new owner data.
     * @returns - observable for request
     */
    create(data: Partial<LegalEntity>) {
        return this.http
            .post<LegalEntity>(`${this.config.get().adminBaseUrl}/v1/owners`, data)
            .pipe(
                tap((owner) =>
                    this.store.add(owner, {
                        prepend: true
                    })
                )
            );
    }

    /** Update - change owner details
     * @param data -
     * @param id -
     * @returns - observable for request
     */
    update(data: LegalEntity, id: LegalEntity['ownerId']) {
        return this.http
            .put<LegalEntity>(`${this.config.get().adminBaseUrl}/v1/owners/${id}`, data)
            .pipe(tap((owner) => this.store.upsert(owner.ownerId, owner)));
    }

    upsert(data: LegalEntity): Observable<LegalEntity> {
        if (data.ownerId) {
            return this.update(data, data.ownerId);
        }
        return this.create(data);
    }

    /** Delete an owner from the system.
     * @param id - owner id
     * @returns - observable for request
     */
    delete(id: LegalEntity['ownerId']) {
        return this.http
            .delete<LegalEntity>(`${this.config.get().adminBaseUrl}/v1/owners/${id}`)
            .pipe(tap(() => this.store.remove(id)));
    }

    /** Disable/Enable (acts as a soft-delete) an owner from the system.
     * @param id - account issuer id
     * @param status - describes the wanted status to be set for the entitiy
     * @returns - observable for request
     */
    toggleEnabled(owner: LegalEntity) {
        return this.http
            .put<LegalEntity>(
                `${this.config.get().adminBaseUrl}/v1/owners/${owner.ownerId}`,
                {
                    ...owner,
                    status:
                        owner.status === OwnerStatus.ENABLED
                            ? OwnerStatus.DISABLED
                            : OwnerStatus.ENABLED
                }
            )
            .pipe(tap((o) => this.store.update(o.ownerId, o)));
    }

    /** toggleSelectedState
     * @description Toggle selected (akita active) state of a legal entity, update active ids.
     * @param legalEntity - legal entity to toggle
     */
    toggleSelectedState(legalEntity: LegalEntity) {
        this.store.toggleActive(legalEntity.ownerId);
    }

    /** setSelectedState
     * @description Set selected (akita active) state of one or more legal entities update active ids.
     * @param legalEntity - legal entity or entities to set selected state for
     * @param selected - selected state
     */
    setSelectedState(legalEntity: LegalEntity | Array<LegalEntity>, selected: boolean) {
        const legalEntityIds = Array.isArray(legalEntity)
            ? legalEntity
                  .filter((entity) => entity.metadata?.includeInGlobalFilter !== false)
                  .map((entity) => entity.ownerId)
            : [legalEntity.ownerId];
        if (selected) {
            this.store.setActive(legalEntityIds);
        } else {
            this.store.removeActive(legalEntityIds);
        }
    }
}
